import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress, Divider, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";

import { EconomicsNPVDataTable } from "./EconomicsNPVDataTable";
import { saveEconomics, selectEconomics } from "../reducers/economicsSlice";
import { AccessTime, Cancel, CloudUpload, ExpandMore, Share } from "@mui/icons-material";
import { formatNumber } from "../helpers/formatNumber";
import { EconomicsChart } from "./EconomicsChart";
import { EconomicsChartOptions } from "./EconomicsChartOptions";

import { selectUser } from "../reducers/userSlice";
import { selectOptions, setIsLoadingData } from "../reducers/optionsSlice";
import { EconomicsDataTable } from "./EconomicsDataTable";
import { EmptyEconomics } from "./EmptyEconomics";
import { selectLists } from "../reducers/listSlice";
import { addProject, selectProjects } from "../reducers/projectSlice";
import { selectWells } from "../reducers/wellSlice";
import { useSnackbar } from "notistack";
import { ShareEconomics } from "./ShareEconomics";
import uuid from "react-uuid";
import { selectFBA } from "../reducers/fbaSlice";
import { useSocket } from "../context/useSocket";
import { EconomicsNPVChart } from "./EconomicsNPVChart";
import { EconomicsVolumesNpvDataTable } from "./EconomicsVolumesNpvDataTable";
import { EconomicsProbableReservesDataTable } from "./EconomicsProbableReservesDataTable";
import { EconomicsReservesNpvDataTable } from "./EconomicsReservesNpvDataTable";
import { EconomicsProductionPricesDataTable } from "./EconomicsProductionPricesDataTable";
import { EconomicsReservesYearsDataTable } from "./EconomicsReservesYearsDataTable";
import { EconomicsReservesCashflowDataTable } from "./EconomicsReservesCashflowDataTable";
import { EconomicsAbandonmentDataTable } from "./EconomicsAbandonmentDataTable";
import { EconomicsAbandonmentNpvDataTable } from "./EconomicsAbandonmentNpvDataTable";
import { EconomicsNI51101Table1Table } from "./EconomicsNI51101Table1Table";
import { EconomicsNI51101Table2Table } from "./EconomicsNI51101Table2Table";
import { EconomicsNI51101Table3Table } from "./EconomicsNI51101Table3Table";
import { EconomicsNI51101Table4Table } from "./EconomicsNI51101Table4Table";
import { EconomicsProductionForecastBOEChart } from "./EconomicsProductionForecastBOEChart";
import { EconomicsProductionForecastOilChart } from "./EconomicsProductionForecastOilChart";
import { EconomicsProductionForecastGasChart } from "./EconomicsProductionForecastGasChart";
import { EconomicsPNGReservesDataTable } from "./EconomicsPNGReservesDataTable";
import { EconomicsProductionPricesRevenueDataTable } from "./EconomicsProductionPricesRevenueDataTable";
import { EconomicsProductionPricesCapitalDataTable } from "./EconomicsProductionPricesCapitalDataTable";
// import { PDFDownloadLink, Document, Page, Text } from '@react-pdf/renderer';



const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.grey[100],
    border: 0,
    color: 'text.primary',
    overflowY: 'scroll',
    flexBasis: 0,
    flexShrink: 1,
    flexGrow: 1,
    padding: 0,
    paddingTop: 10,
    margin: 0,
    marginTop: 10,
  },
  header: {
    padding: '0 10px',
    paddingTop: 10,
    backgroundColor: theme.palette.grey[200],
    position: 'relative'
  },
  tabs: {
    backgroundColor: theme.palette.grey[200],
    borderBottom: '1px solid lightgrey',
  },
  cards: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  card: {
    margin: '0 5px',
    height: 120,
    padding: 10,
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: 'auto',
    maxWidth: 300,
  },
  button: {
    borderRadius: '50%'
  },
  status: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: 10
  },
  icon: {
    color: theme.palette.text.secondary
  }
}));

export const Economics = () => {

  const { enqueueSnackbar } = useSnackbar();

  const classes = useStyles();
  const theme = useTheme();
  const options = useSelector(selectOptions);
  const projects = useSelector(selectProjects);
  const currentProject = projects.byId[projects.currentProjectId];
  const user = useSelector(selectUser);
  const lists = useSelector(selectLists);
  const wells = useSelector(selectWells);
  const economics = useSelector(selectEconomics);
  const currentEconomics = economics?.byId?.[economics?.currentEconomicsId];
  const fba = useSelector(selectFBA);

  const [tabItem, setTabItem] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [shareEconomicsVisible, setShareEconomicsVisible] = useState(false);
  const [cashflowType, setCashflowType] = useState('net');

  const dispatch = useDispatch();

  const { socket } = useSocket();

  const darkMode = user?.value?.darkMode;
  const demoUser = user?.value?.subscription === 'Demo';


  try {
    return (
      <div className={classes.root} id="economics" style={{backgroundColor: theme?.palette?.paper?.[darkMode ? 'dark' : 'light']}}>
        
        {shareEconomicsVisible && <ShareEconomics 
          setShareEconomicsVisible={setShareEconomicsVisible} 
          handleCopyEconomicsLink={handleCopyEconomicsLink} 
          handleExportEconomicsJSONClick={handleExportEconomicsJSONClick} 
        />}

        {
        (currentEconomics && currentEconomics?.cashflowNet && currentEconomics?.cashflowGross) ?
        <>
          <Box style={{boxShadow: `0px 2px 6px ${darkMode ? '#222' : 'lightgrey'}`}}>
            <div className={classes.header} style={{backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light}}>
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                <div style={{display: 'flex', flexDirection: 'column'}}>
                  <Typography color="textSecondary" style={{fontSize: 14}}>
                    ECONOMICS FORECAST
                  </Typography>
                  <Typography variant="h5">
                    {currentEconomics?.name || ""}
                  </Typography>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                  <div style={{display: 'flex'}}>
                    <div style={{position: 'relative'}}>
                      <Tooltip title="Save project">
                        <span><Button 
                          startIcon={<CloudUpload/>} 
                          color="primary" 
                          variant="outlined" 
                          size="small" 
                          style={{marginRight: 10, height: 28}} 
                          onClick={() => handleSaveProject(currentProject)}
                          disabled={demoUser || isLoading || user?.value?.email !== projects?.byId[projects?.currentProjectId]?.createdBy}
                        >
                          Save
                        </Button></span>
                      </Tooltip>
                      {isLoading && (
                        <CircularProgress
                          size={24}
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            marginTop: '-11px',
                            marginLeft: '-12px',
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
                    <AccessTime color="textSecondary" fontSize="14px" style={{ marginRight: 5 }} />
                    <Typography color="textSecondary" fontSize="14px" textAlign={'right'}>Last saved: {new Date(currentProject?.lastModified)?.toLocaleString()}</Typography>
                  </div>
                </div>
              </div>

              <div style={{paddingLeft: 10, paddingBottom: 10, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', position: 'absolute', top: 10, right: 10, backgroundColor: darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light}}>
                <div style={{position: 'relative'}}>

                  <Tooltip title="Share Economics">
                    <span><Button 
                      startIcon={<Share />} 
                      onClick={() => setShareEconomicsVisible(true)}
                      variant="outlined"
                      size="small"
                      style={{height: 28, marginRight: 10}}
                    >
                      Share
                    </Button></span>
                  </Tooltip>

                  <Tooltip title="Save Project">
                    <span><Button 
                      startIcon={<CloudUpload />} 
                      size="small"
                      variant="contained" 
                      style={{color: darkMode ? '#000' : '#fff', height: 28}}
                      color={darkMode ? "inherit" : "primary"}
                      edge="end" 
                      onClick={() => handleSaveProject(currentProject)}
                      disabled={demoUser || isLoading || user?.value?.email !== projects?.byId[projects?.currentProjectId]?.createdBy}
                    >
                      Save Project
                    </Button></span>              
                  </Tooltip>

                  {isLoading && (
                    <CircularProgress
                      size={24}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        marginTop: '-11px',
                        marginLeft: '40px',
                      }}
                    />
                  )}
                </div>

                <div style={{display: 'flex', alignItems: 'center', marginTop: 5}}>
                  <AccessTime color="textSecondary" fontSize="14px" style={{ marginRight: 5 }} />
                  <Typography color="textSecondary" fontSize="14px" textAlign={'right'}>Last saved: {new Date(currentProject?.lastModified)?.toLocaleString()}</Typography>
                </div>
              
              </div>

            </div>

            <Tabs
              className={classes.tabs}
              value={tabItem}
              TabIndicatorProps={{style: {background: darkMode ? '#fff' : theme.palette.primary.main}}}
              textColor={darkMode ? "inherit" : "primary"}
              onChange={(e, value) => value >= 0 && setTabItem(value)}
              style={{
                backgroundColor: (darkMode ? theme?.palette?.elevationOne?.dark : theme?.palette?.elevationTwo?.light),
                borderBottom: `1px solid ${darkMode ? 'grey' : 'lightgrey'}`,
              }}
            >
              <Tab label="Full Forecast" />
              <Tab label="Quick Look" disabled/>
            </Tabs>
          </Box>

          <FullEconomics />

        </> 
        :
        <EmptyEconomics />
        }

        {options?.isLoadingData && 
          <div style={{backgroundColor: darkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.75)', position: 'absolute', left: 0, top: 0, zIndex: 420, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%'}}>
            <Typography style={{marginBottom: 20}}>Calculating economics...</Typography>
            <CircularProgress></CircularProgress>
            <IconButton style={{position: 'absolute', bottom: '60px'}} onClick={() => dispatch(setIsLoadingData(false))}><Cancel color="grey" /> </IconButton>
        </div>}
      </div>
    );

    function FullEconomics() {
      return (
      <>
        <Accordion 
          defaultExpanded 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none', display: 'flex'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography noWrap>Economics Summary</Typography>
              <GrossNetTag type={cashflowType} />
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsChartOptions cashflowType={cashflowType} setCashflowType={setCashflowType} />
            <div style={{height: options?.chartOrientation === 'Horizontal' ? '500px' : '1000px'}}>
              <EconomicsChart cashflowType={cashflowType} />
            </div>

            <Typography variant="caption">Before Tax</Typography>
            <EconomicsSummary isAfterTax={false} />
            <Divider style={{marginBottom: 10}} />
            <Typography variant="caption">After Tax</Typography>
            <EconomicsSummary isAfterTax={true} />
            <Divider style={{marginBottom: 10}} />
            <Typography variant="caption">Economic Volumes</Typography>
            <EconomicsVolumesSummary type={cashflowType} />


          </AccordionDetails>
        </Accordion>
        
        <Accordion 
          defaultExpanded={false}
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography noWrap>Net Present Values</Typography>
              <GrossNetTag type={cashflowType} />
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsNPVChart cashflowType={cashflowType} />
            <EconomicsNPVDataTable npv={cashflowType === 'net' ? currentEconomics?.npvNet : currentEconomics?.npvGross} currency={currentEconomics?.settings?.currency} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false}
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Economic Volumes and Net Present Values</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsVolumesNpvDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Summary of Reserves and Net Present Values</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsProbableReservesDataTable economics={currentEconomics} />
            <EconomicsReservesYearsDataTable economics={currentEconomics} />
            <EconomicsReservesNpvDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Summary of Reserves and Cash Flow</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsReservesCashflowDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Abandonment and Reclamation</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsAbandonmentDataTable economics={currentEconomics} />
            <EconomicsAbandonmentNpvDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Production Forecast Charts</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsProductionForecastBOEChart />
            <EconomicsProductionForecastOilChart />
            <EconomicsProductionForecastGasChart />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Production and Price Forecast</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsProductionPricesDataTable economics={currentEconomics} />
            <EconomicsProductionPricesRevenueDataTable economics={currentEconomics} />
            <EconomicsProductionPricesCapitalDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>Summary of the Evaluation of the P. & N.G. Reserves</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsPNGReservesDataTable economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <Typography noWrap>National Instrument 51-101</Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 5}}>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>National Instrument 51-101 - Table 1</Typography>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>As Of Date: {currentEconomics?.settings?.startDate?.length > 0 ? new Date(currentEconomics?.settings?.startDate)?.toISOString()?.split('T')?.[0] : ''}</Typography>
            </div>            
            <EconomicsNI51101Table1Table economics={currentEconomics} />
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 5}}>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>National Instrument 51-101 - Table 2</Typography>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>As Of Date: {currentEconomics?.settings?.startDate?.length > 0 ? new Date(currentEconomics?.settings?.startDate)?.toISOString()?.split('T')?.[0] : ''}</Typography>
            </div>
            <EconomicsNI51101Table2Table economics={currentEconomics} />
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 5}}>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>National Instrument 51-101 - Table 3</Typography>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>As Of Date: {currentEconomics?.settings?.startDate?.length > 0 ? new Date(currentEconomics?.settings?.startDate)?.toISOString()?.split('T')?.[0] : ''}</Typography>
            </div>
            <EconomicsNI51101Table3Table economics={currentEconomics} />
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 5}}>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>National Instrument 51-101 - Table 4</Typography>
              <Typography sx={{marginTop: 2, fontSize: 12, fontWeight: 'bold'}}>As Of Date: {currentEconomics?.settings?.startDate?.length > 0 ? new Date(currentEconomics?.settings?.startDate)?.toISOString()?.split('T')?.[0] : ''}</Typography>
            </div>
            <EconomicsNI51101Table4Table economics={currentEconomics} />
          </AccordionDetails>
        </Accordion>

        <Accordion 
          defaultExpanded={false} 
          style={{
            margin: 10,
            borderRadius: 4,
            border: 'none',
            backgroundColor: theme?.palette?.elevationOne?.[darkMode ? 'dark' : 'light'],
          }}
          elevation={0}
          sx={{boxShadow: 1}}
        >
          <AccordionSummary expandIcon={<ExpandMore />} style={{border: 'none'}}>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
              <Typography noWrap>Cash Flow Data</Typography>
              <GrossNetTag type={cashflowType} />
            </div>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <EconomicsDataTable 
              cashflow={cashflowType === 'net' ? currentEconomics?.cashflowNet : currentEconomics?.cashflowGross} 
              forecast={cashflowType === 'net' ? currentEconomics?.forecastNet : currentEconomics?.forecastGross} 
              settings={currentEconomics?.settings} />
          </AccordionDetails>
        </Accordion>
      </>  
    )
    };


    function EconomicsSummary({ isAfterTax }) {
      try {
        return (
          <div className={classes.cards}>
            <div className={classes.card}>
              <Typography variant="body2">PAYOUT</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{isAfterTax ? currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.payoutATax : currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.payout || "-"}</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">ROI</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{(isAfterTax ? currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.roiATax : currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.roi)?.toFixed(2) || "-"}</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">NPV10</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{"$"+(!isNaN(isAfterTax ? currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowATax?.[2] : currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowBTax?.[2]) ? formatNumber(+((isAfterTax ? currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowATax?.[2] : currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.cashflowBTax?.[2]) / 1000000).toFixed(2)) : 0)} MM</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>{currentEconomics?.settings?.currency === 'cad' ? 'CAD' : 'USD'}</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">PI10</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{(isAfterTax ? currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.pi10ATax : currentEconomics?.[cashflowType === 'net' ? 'npvNet' : 'npvGross']?.pi10)?.toFixed(2) || "-"}</b></span>
              </Typography>
            </div>
            
            
            
          </div>
        );  
      } catch (error) {
        console.log(error)
        return <></>;
      };
    };

    function EconomicsVolumesSummary({ type }) {
      try {
        return (
          <div className={classes.cards}>
            <div className={classes.card}>
              <Typography variant="body2">OIL</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{((currentEconomics?.[type === 'net' ? 'economicVolumesNet' : 'economicVolumesGross']?.oil || 0) / 1000)?.toFixed(2) || "-"} M</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>bbl</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">RAW GAS</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{((currentEconomics?.[type === 'net' ? 'economicVolumesNet' : 'economicVolumesGross']?.rawGas || 0) / 1000)?.toFixed(2) || "-"} B</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>scf</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">SALES GAS</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{((currentEconomics?.[type === 'net' ? 'economicVolumesNet' : 'economicVolumesGross']?.salesGas || 0) / 1000)?.toFixed(2) || "-"} B</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>scf</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">NGL</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{((currentEconomics?.[type === 'net' ? 'economicVolumesNet' : 'economicVolumesGross']?.ngl || 0) / 1000)?.toFixed(2) || "-"} M</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>bbl</b></span>
              </Typography>
            </div>
            <div className={classes.card}>
              <Typography variant="body2">CONDENSATE</Typography>
              <Typography variant="h5" align='center' style={{marginTop: 10}}>
                <span><b>{((currentEconomics?.[type === 'net' ? 'economicVolumesNet' : 'economicVolumesGross']?.condensate || 0) / 1000)?.toFixed(2) || "-"} M</b></span>
                <span style={{fontSize: 16, marginLeft: 10}}><b>bbl</b></span>
              </Typography>
            </div>
          </div>
        );  
      } catch (error) {
        console.log(error)
        return <></>;
      };
    };
  
    function handleSaveProject(project) {
      try {
        if (!project) return;

        console.log('Saving project ' + project.name);
        
        setIsLoading(true);

        let newWells = project?.wells?.length > 0 ? [...project.wells] : [];

        // save all project economics
        const economicsIds = economics?.allIds?.filter(id => economics?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
        if (economicsIds?.length > 0) {
          for (let economicsId of economicsIds) {
            if (economics?.byId?.[economicsId]?.forecastNet && economics?.byId?.[economicsId]?.cashflowNet && economics?.byId?.[economicsId]?.cashflowGross) {
              dispatch(saveEconomics(economics?.byId?.[economicsId]));
            };
          };
        };

        // save all project fbas
        const fbaIds = fba?.allIds?.filter(id => fba?.byId?.[id]?.projectId === currentProject?.id); // only keep economicsIds that correspond to the current project
        if (fbaIds?.length > 0 && socket && socket.readyState === 1) {
          for (let fbaId of fbaIds) {
            const currFBA = fba?.byId?.[fbaId];
            if (currFBA?.fbaData) {

              socket.send(JSON.stringify({
                messageType: 'saveFBA', 
                fba: currFBA, 
                fileName: currFBA?.id, 
                userEmail: user?.value?.email
              }));

            };
          };
        };
  
        // compile a list of UWIs in the project
        let projectUwis = [];
  
        project.listIds.forEach(listId => {
  
          lists.byId[listId]?.wellUwis?.forEach(wellUwi => {
            projectUwis.push(wellUwi);
          });
  
        });
  
        for (let wellUwi of projectUwis) { // save local params
          try {
            if (wells?.byUwi?.[wellUwi]?.status === 'processed' && wells?.byUwi?.[wellUwi]?.params && !wells?.byUwi?.[wellUwi]?.local) { // if params are available, well is processed
              let wellParams = {...wells?.byUwi?.[wellUwi]?.params};
              const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
      
              for (let fluid of wellFluids) {
                wellParams[fluid] = {...wellParams?.[fluid], outliers: []};
              };
  
              const wellIndex = newWells.map(w => w.uwi)?.indexOf(wellUwi);
              if (wellIndex === -1) { // if well hasnt been saved before, add it
                newWells.push({uwi: wellUwi, params: wellParams});
              } else { // update existing saved well with new params
                newWells.splice(wellIndex, 1, {uwi: wellUwi, params: wellParams});
              };
            };
            
          } catch (error) {
            console.log(error);
          };
        };
  
        const newProject = {
          id: project?.id,
          name: project?.name, 
          color: project?.color,
          description: project?.description || "",
          createdBy: project?.createdBy,
          lastModified: new Date().toLocaleString(),
          createdOn: project?.createdOn,
          listIds: project?.listIds || [],
          wells: newWells,
          currentListId: project?.currentListId || "",
          economicsIds: economicsIds?.length > 0 ? economicsIds : (project?.economicsIds || []),
          fbaIds: fbaIds?.length > 0 ? fbaIds : (project?.fbaIds || []),
          public: project?.public || false
        };
  
        const newLists = project?.listIds?.length > 0 ? project?.listIds?.map(listId => {
          const currentList = lists.byId[listId];
          return {
            id: listId,
            name: currentList?.name,
            createdBy: currentList?.createdBy,
            lastModified: new Date().toLocaleString(),
            color: currentList?.color,
            description: currentList?.description || "",
            wellUwis: currentList?.wellUwis.filter(uwi => !wells.byUwi[uwi]?.local),
            typeCurveId: currentList?.typeCurveId,
            groupWellId: currentList?.groupWellId,
            forecastListTab: currentList?.forecastListTab,
            filters: currentList?.filters
          };
        }) : [];

        dispatch(addProject({ project: newProject, lists: newLists, email: user?.value?.email })).then(() => {
          setIsLoading(false);
          enqueueSnackbar(`Project ${project?.name} saved successfully`, { variant: 'success' });
        });
        
      } catch (error) {
        console.log(error);
        enqueueSnackbar(`Could not save project ${project?.name}`, { variant: 'error' });
        return;
      };
      
    };

    function handleCopyEconomicsLink() {
      try {
        let copyText = currentEconomics?.location;
        if (!copyText) return;
        navigator.clipboard.writeText(copyText);
      
        enqueueSnackbar('Economics link copied to clipboard', {
          variant: 'info'
        });
  
        setShareEconomicsVisible(false);
      } catch (error) {
        console.log(error);
      };
    };

    function handleExportEconomicsJSONClick() {
      try {
        let dataStr = JSON.stringify({...currentEconomics, id: uuid(), public: false, createdBy: user?.value?.email});
        if (!dataStr?.length > 0) return;

        let dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const date = new Date().toLocaleDateString();
        let exportFileDefaultName = `${currentEconomics?.name}_${date}.json`;
        let linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
        
        setShareEconomicsVisible(false);
      } catch (error) {
        console.log(error);
        return;
      };
    };

    function GrossNetTag({ type }) {
      return (
        <div style={{width: 38, height: 12, marginLeft: 10, borderRadius: '4px', outline: '1px solid #0758B7', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Typography style={{fontSize: 10, fontWeight: 'bold', color: '#0758B7'}}>{type?.toUpperCase()}</Typography>
        </div>
      );
    };
  } catch (error) {
    console.log(error);
    return (<div style={{width: '100%'}}></div>);
  };
};
